import { useEffect, useState, useCallback } from "react";
import { Star, Check, X, Trash2 } from "lucide-react";
import { toast } from "sonner";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function AdminReviews() {
  const [reviews, setReviews] = useState([]);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await fetch(`${API}/reviews/all`, { credentials: "include" });
      if (!res.ok) throw new Error();
      setReviews(await res.json());
    } catch {
      toast.error("Could not load reviews.");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const setApproved = async (id, approved) => {
    try {
      const res = await fetch(`${API}/reviews/${id}/approve`, {
        method: "PATCH",
        credentials: "include",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ approved }),
      });
      if (!res.ok) throw new Error();
      setReviews((rs) => rs.map((r) => (r.id === id ? { ...r, approved } : r)));
      toast.success(approved ? "Review published." : "Review hidden.");
    } catch {
      toast.error("Update failed.");
    }
  };

  const remove = async (id) => {
    try {
      if (!window.confirm("Permanently delete this review?")) return;
      const res = await fetch(`${API}/reviews/${id}`, { method: "DELETE", credentials: "include" });
      if (!res.ok) throw new Error();
      setReviews((rs) => rs.filter((r) => r.id !== id));
      toast.success("Review deleted.");
    } catch {
      toast.error("Delete failed.");
    }
  };

  return (
    <section data-testid="admin-reviews-section">
      <h1 className="font-bebas text-4xl sm:text-5xl chrome-text mb-2">CUSTOMER REVIEWS</h1>
      <p className="text-slate-400 text-sm mb-6" data-testid="admin-review-count">{reviews.filter((review) => !review.approved).length} awaiting approval</p>
      <div className="space-y-4" data-testid="admin-reviews-list">
        {reviews.length === 0 && !loading && (
          <p className="text-slate-500" data-testid="admin-reviews-empty">No reviews yet.</p>
        )}
        {reviews.map((r) => (
          <div key={r.id} className="metal-card rounded-2xl p-5 flex flex-wrap items-center gap-4" data-testid={`admin-review-${r.id}`}>
            <div className="flex-1 min-w-[220px]">
              <div className="flex flex-wrap items-center gap-2">
                <div className="flex gap-0.5">
                  {[1, 2, 3, 4, 5].map((n) => (
                    <Star key={n} className={`w-3.5 h-3.5 ${n <= r.rating ? "text-electric fill-electric" : "text-slate-600"}`} />
                  ))}
                </div>
                <span className="text-white font-semibold text-sm">{r.name}</span>
                {r.location && <span className="text-slate-500 text-xs">• {r.location}</span>}
                <span data-testid={`review-status-${r.id}`} className={`text-xs ${r.approved ? "text-emerald-400" : "text-amber-300"}`}>{r.approved ? "Published" : "Pending approval"}</span>
              </div>
              <p data-testid={`admin-review-text-${r.id}`} className="mt-1.5 text-slate-400 text-sm font-light whitespace-pre-wrap break-words">“{r.text}”</p>
            </div>
            <div className="flex items-center gap-2">
              {r.approved ? (
                <button data-testid={`review-hide-${r.id}`} onClick={() => setApproved(r.id, false)} className="flex items-center gap-1.5 text-xs border border-slate-600 rounded-full px-4 py-2 text-slate-300 hover:border-yellow-400 hover:text-yellow-300 transition-colors">
                  <X className="w-3.5 h-3.5" /> Hide
                </button>
              ) : (
                <button data-testid={`review-approve-${r.id}`} onClick={() => setApproved(r.id, true)} className="flex items-center gap-1.5 text-xs border border-electric/50 rounded-full px-4 py-2 text-electric hover:bg-electric/10 transition-colors">
                  <Check className="w-3.5 h-3.5" /> Approve
                </button>
              )}
              <button data-testid={`review-delete-${r.id}`} onClick={() => remove(r.id)} className="flex items-center gap-1.5 text-xs border border-slate-600 rounded-full px-4 py-2 text-slate-300 hover:border-red-500 hover:text-red-400 transition-colors">
                <Trash2 className="w-3.5 h-3.5" /> Delete
              </button>
            </div>
          </div>
        ))}
      </div>
    </section>
  );
}
